import { Banner } from "@shopify/polaris";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useVerifiedShopGeneration } from "../ShopGenerationBoundary";
import {
  listPurchaseOrderEmailDeliveries,
  purchaseOrderEmailReconciliationKeys,
} from "../api/purchase_order_email_reconciliation";
import { t } from "../lib/i18n";
import {
  listPurchaseOrderEmailAttempts,
  subscribePurchaseOrderEmailAttempts,
} from "../utils/purchaseOrderEmailAttempt";

const NOTICE_REFRESH_MS = 60_000;

/** Keep uncertain supplier-email outcomes visible from every app screen. */
export function PurchaseOrderEmailReconciliationNotice() {
  const navigate = useNavigate();
  const { shopGeneration } = useVerifiedShopGeneration();
  const [localAttempts, setLocalAttempts] = useState(() =>
    listPurchaseOrderEmailAttempts(shopGeneration),
  );
  useEffect(() => {
    setLocalAttempts(listPurchaseOrderEmailAttempts(shopGeneration));
    return subscribePurchaseOrderEmailAttempts(shopGeneration, () =>
      setLocalAttempts(listPurchaseOrderEmailAttempts(shopGeneration)),
    );
  }, [shopGeneration]);
  const query = useQuery({
    queryKey: purchaseOrderEmailReconciliationKeys.unresolved,
    queryFn: () => listPurchaseOrderEmailDeliveries(false),
    refetchInterval: NOTICE_REFRESH_MS,
  });

  const openPending = () => {
    const first = localAttempts[0];
    const deliveries = query.data?.deliveries ?? [];
    if (
      deliveries.length === 1 &&
      localAttempts.every(
        (attempt) =>
          attempt.supplierId === deliveries[0].supplier_id &&
          attempt.preview.draft_sha === deliveries[0].draft_sha &&
          attempt.key === deliveries[0].idempotency_key,
      )
    ) {
      navigate(`/purchase-order-email-reconciliation/${deliveries[0].id}`);
      return;
    }
    if (deliveries.length > 0 || first === undefined) {
      navigate("/purchase-order-email-reconciliation");
      return;
    }
    if (
      first.disposition === "reconciliation_required" &&
      first.deliveryId !== null
    ) {
      navigate(`/purchase-order-email-reconciliation/${first.deliveryId}`);
      return;
    }
    const params = new URLSearchParams({ supplier: String(first.supplierId) });
    if (first.requestedLocationId !== null) {
      params.set("location", String(first.requestedLocationId));
    }
    navigate(`/purchase-orders?${params.toString()}`);
  };

  if (query.isLoading && localAttempts.length === 0) return null;
  if (query.isError) {
    return (
      <Banner
        tone="warning"
        title={t("発注書メールの送信状況を取得できませんでした")}
        action={{
          content: localAttempts.length > 0 ? t("保存済みの送信を確認") : t("再試行"),
          onAction:
            localAttempts.length > 0 ? openPending : () => void query.refetch(),
        }}
      >
        {localAttempts.length > 0
          ? t("このタブに結果未確定の発注書メールが保存されています。新しいメールを作らず、保存済みの送信を確認してください。")
          : t("送信結果が不明な発注書メールが無いか確認できていません。通信環境を確認して再試行してください。")}
      </Banner>
    );
  }

  const signatures = new Set(
    (query.data?.deliveries ?? []).map(
      (delivery) =>
        `${delivery.supplier_id}:${delivery.draft_sha}:${delivery.idempotency_key}`,
    ),
  );
  for (const attempt of localAttempts) {
    signatures.add(
      `${attempt.supplierId}:${attempt.preview.draft_sha}:${attempt.key}`,
    );
  }
  const count = signatures.size;
  if (count === 0) return null;
  return (
    <Banner
      tone="critical"
      title={t("発注書メールの送信確認が必要です")}
      action={{
        content:
          count === 1
            ? t("このメールの送信結果を確認")
            : t("{count} 件の送信結果を確認", { count }),
        onAction: openPending,
      }}
    >
      {t("送信結果を確定できていない発注書メールが {count} 件あります。ボタンを押して保存内容を開き、仕入先に受信を確認してください。確認できない場合は画面からお問い合わせできます。", {
        count,
      })}
    </Banner>
  );
}
